import * as Sentry from "@sentry/nextjs";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

/**
 * Header carrying the per-request correlation id. The frontend generates it in
 * the axios request interceptor (see `startHttpSpan` in `axios-helper.ts`) and
 * the backend echoes it + tags it on its logs/Sentry events, so a user-facing
 * error toast can be traced to the exact backend request that failed.
 */
export const REQUEST_ID_HEADER = "X-Request-ID";

/**
 * Marker tag set on every event captured through `captureApiError`. The Sentry
 * `beforeSend` sampling bypass (see `sentry.shared.config.ts`) keeps events
 * carrying this tag at 100% while everything else stays sampled — so a
 * user's save/submit failure is never dropped in production.
 */
export const CRITICAL_ERROR_TAG = "medly.critical";

/** Coarse failure mode of a network request, used as a Sentry tag/facet. */
export type ApiErrorKind =
  | "offline"
  | "timeout"
  | "network"
  | "client_4xx"
  | "server_5xx"
  | "unknown";

/**
 * Classify an error thrown by an axios client into a coarse failure mode so we
 * can tell "the user was offline" from "the server 500'd" from "bad request"
 * at a glance in Sentry.
 */
export function classifyAxiosError(error: unknown): ApiErrorKind {
  if (!axios.isAxiosError(error)) return "unknown";

  const status = error.response?.status;
  if (typeof status === "number") {
    if (status >= 500) return "server_5xx";
    if (status >= 400) return "client_4xx";
  }

  // No usable response → the request never completed. Distinguish the reasons.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "offline";
  }
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    return "timeout";
  }
  if (error.code === "ERR_NETWORK" || error.request) {
    return "network";
  }
  return "unknown";
}

interface CaptureApiErrorOptions {
  /** Kebab/snake action tag, e.g. `save_manager_priority`, `finish_session`. */
  action: string;
  /** Extra key/values merged into the `api_error` Sentry context. */
  extra?: Record<string, unknown>;
}

/** Serialize a response body to a bounded string so we never ship a huge blob. */
function truncateBody(data: unknown): string | undefined {
  if (data === undefined || data === null) return undefined;
  let str: string;
  try {
    str = typeof data === "string" ? data : JSON.stringify(data);
  } catch {
    str = String(data);
  }
  return str.length > 1000 ? `${str.slice(0, 1000)}…` : str;
}

/** Best-effort request path (query stripped) for grouping/tagging. */
function requestPath(config: InternalAxiosRequestConfig | undefined): string {
  const url = config?.url;
  if (!url) return "unknown";
  const full = url.startsWith("http") ? url : `${config?.baseURL ?? ""}${url}`;
  try {
    return new URL(full, "http://relative.invalid").pathname;
  } catch {
    return url.split("?")[0];
  }
}

function readRequestId(
  config: InternalAxiosRequestConfig | undefined,
): string | undefined {
  const raw = config?.headers?.[REQUEST_ID_HEADER];
  return typeof raw === "string" && raw ? raw : undefined;
}

interface AxiosErrorDetails {
  status?: number;
  method?: string;
  endpoint: string;
  requestId?: string;
  code?: string;
  timeoutMs?: number;
  responseBody?: string;
}

/** Pull the diagnostic fields off an axios error (all undefined for non-axios). */
function extractAxiosDetails(error: unknown): AxiosErrorDetails {
  const axiosError = axios.isAxiosError(error)
    ? (error as AxiosError)
    : undefined;
  const config = axiosError?.config;
  return {
    status: axiosError?.response?.status,
    method: config?.method?.toUpperCase(),
    endpoint: requestPath(config),
    requestId: readRequestId(config),
    code: axiosError?.code,
    timeoutMs: config?.timeout,
    responseBody: truncateBody(axiosError?.response?.data),
  };
}

/**
 * Capture an API error to Sentry with the diagnostic context needed to tell
 * *why* a request failed for a specific user: failure classification, HTTP
 * status, endpoint, a truncated response body, and the X-Request-ID that ties
 * it to the backend log line. Groups by action + endpoint + status/kind so
 * distinct failure modes don't collapse into one issue.
 *
 * Accepts `unknown` and works for non-axios errors too (they classify as
 * `unknown` and are still captured with the action tag).
 */
export function captureApiError(
  error: unknown,
  { action, extra }: CaptureApiErrorOptions,
): void {
  const kind = classifyAxiosError(error);
  const d = extractAxiosDetails(error);

  Sentry.captureException(error, {
    tags: {
      action,
      "error.kind": kind,
      "http.status": d.status ?? "none",
      "api.endpoint": d.endpoint,
      request_id: d.requestId ?? "unknown",
      [CRITICAL_ERROR_TAG]: "true",
    },
    contexts: {
      api_error: {
        kind,
        action,
        method: d.method,
        endpoint: d.endpoint,
        status: d.status,
        code: d.code,
        timeout_ms: d.timeoutMs,
        request_id: d.requestId,
        response_body: d.responseBody,
        ...extra,
      },
    },
    // Group by failure mode + endpoint so "500 on save" and "offline on save"
    // are separate issues instead of one noisy bucket.
    fingerprint: [action, d.endpoint, String(d.status ?? kind)],
  });
}
